import React, { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Ant Design Table header cell with both drag-to-reorder (via @dnd-kit/sortable)
 * and column-resize (same mousedown pattern as ResizableTableHeaderCell).
 *
 * Props injected by Ant Table + our column config:
 *   - dndId:     sortable identifier (framework key), falsy → not draggable
 *   - onResize:  resize callback, falsy → not resizable
 *   - width / minWidth: for resize math
 */
const DraggableHeaderCell = React.forwardRef((props, ref) => {
    const {
        children,
        dndId,
        onResize,
        width,
        minWidth = 48,
        className,
        style,
        ...rest
    } = props;

    const sortable = useSortable({ id: dndId || '__noop__', disabled: !dndId });
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

    const onResizeMouseDown = useCallback(
        (e) => {
            if (typeof onResize !== 'function') return;
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startW = typeof width === 'number' ? width : minWidth;
            const onMove = (ev) => {
                const delta = ev.clientX - startX;
                onResize(Math.max(minWidth, Math.round(startW + delta)));
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [width, minWidth, onResize]
    );

    const mergedStyle = {
        ...style,
        position: 'relative',
        ...(dndId
            ? {
                  transform: CSS.Translate.toString(transform),
                  transition,
                  opacity: isDragging ? 0.5 : 1,
                  cursor: 'grab',
              }
            : {}),
    };

    return (
        <th
            ref={(node) => {
                if (dndId) setNodeRef(node);
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
            }}
            className={className}
            style={mergedStyle}
            {...rest}
            {...(dndId ? { ...attributes, ...listeners } : {})}
        >
            {children}
            {typeof onResize === 'function' && (
                <span
                    className="policylens-col-resize-handle"
                    title="Drag to resize column"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize column"
                    onMouseDown={onResizeMouseDown}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                />
            )}
        </th>
    );
});

DraggableHeaderCell.displayName = 'DraggableHeaderCell';

export default DraggableHeaderCell;
